import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api/client.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { Loader, Alert, Empty, ProgressBar, relativeTime } from '../../components/ui.jsx';
import CubMascot from '../../components/CubMascot.jsx';
import { useSchoolScope } from './SchoolScope.jsx';

export default function AdminStudents() {
  const { user } = useAuth();
  const { schoolId, picker } = useSchoolScope();

  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [grade, setGrade] = useState('all');
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState('name');

  useEffect(() => {
    if (user.role === 'superadmin' && !schoolId) {
      setData(null);
      return undefined;
    }
    // Small debounce so typing in the search box does not hammer the API.
    const t = setTimeout(() => {
      api
        .adminStudents({ schoolId, search, grade, status, sort })
        .then(setData)
        .catch((err) => setError(err.message));
    }, 250);
    return () => clearTimeout(t);
  }, [schoolId, search, grade, status, sort, user.role]);

  const exportCsv = () => {
    if (!data?.students.length) return;
    const header = ['User ID', 'Name', 'Grade', 'Lessons completed', 'XP', 'Avg score', 'Minutes', 'Last login'];
    const rows = data.students.map((s) => [
      s.userId,
      s.fullName,
      s.grade ?? '',
      s.lessonsCompleted,
      s.xp,
      s.avgScore,
      s.minutes,
      s.lastLoginAt ? new Date(s.lastLoginAt).toISOString() : 'never',
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `cybercub-students-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>🎒 Students</h1>
          <p>Every child at your school, with how far they have got.</p>
        </div>
        {picker}
      </div>

      {error && <Alert kind="bad" onDismiss={() => setError('')}>{error}</Alert>}

      <div className="filter-bar">
        <input
          type="search"
          placeholder="🔍 Search by name, User ID or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search students"
        />
        <select value={grade} onChange={(e) => setGrade(e.target.value)} aria-label="Filter by grade">
          <option value="all">All grades</option>
          {data?.grades?.map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
          <option value="all">All accounts</option>
          <option value="active">Active only</option>
          <option value="paused">Paused only</option>
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort by">
          <option value="name">Sort: Name</option>
          <option value="progress">Sort: Most progress</option>
          <option value="xp">Sort: Most XP</option>
          <option value="recent">Sort: Recently active</option>
        </select>
        <button type="button" className="btn btn-sm btn-ghost" onClick={exportCsv} disabled={!data?.students.length}>
          ⬇ Export CSV
        </button>
      </div>

      {!data ? (
        <Loader label="Loading the roster…" />
      ) : data.students.length === 0 ? (
        <Empty
          icon="🎒"
          title="No students found"
          text="Try clearing the filters, or ask a superadmin to register students for this school."
        />
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Grade</th>
                <th style={{ minWidth: 170 }}>Progress</th>
                <th className="num">XP</th>
                <th className="num">Avg score</th>
                <th className="num">Minutes</th>
                <th>Last active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.students.map((s) => (
                <tr key={s.id}>
                  <td>
                    <div className="cell-user">
                      <CubMascot avatar={s.avatar} size={38} />
                      <div>
                        <strong>{s.fullName}</strong>
                        <span className="faint small">{s.userId}</span>
                      </div>
                      {!s.isActive && <span className="pill pill-bad">Paused</span>}
                    </div>
                  </td>
                  <td>{s.grade ?? '—'}</td>
                  <td>
                    <ProgressBar
                      value={s.lessonsCompleted}
                      max={data.totalLessons}
                      tone="mint"
                      label={`${s.lessonsCompleted}/${data.totalLessons}`}
                    />
                  </td>
                  <td className="num">{s.xp}</td>
                  <td className="num">{s.avgScore || '—'}</td>
                  <td className="num">{s.minutes}</td>
                  <td className="small muted">{relativeTime(s.lastLoginAt)}</td>
                  <td>
                    <Link to={`/admin/students/${s.id}`} className="btn btn-sm btn-ghost">
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
